import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Observable, of, shareReplay, tap } from 'rxjs';
import { UserCreditsData, UserCreditsService } from './user-credits.service';
import { SOCKET_EVENTS, SocketService } from '../../../@core/services/socket.service';

@Injectable({ providedIn: 'root' })
export class UserCreditsStateService {
    private creditsService = inject(UserCreditsService);
    private socketService = inject(SocketService);
    private destroyRef = inject(DestroyRef);

    private _credits = signal<UserCreditsData | null>(null);
    private creditsCache$?: Observable<any>;

    constructor() {
        this.socketService.on<{ userId?: string; orgId?: string }>(SOCKET_EVENTS.CREDITS_UPDATED)
            .pipe(
                debounceTime(1500),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.refreshCredits().subscribe());
    }

    public readonly credits = this._credits.asReadonly();
    public readonly askRemaining = computed(() => this._credits()?.ask.remaining ?? 0);
    public readonly agentRemaining = computed(() => this._credits()?.agent.remaining ?? 0);
    /** True only once credits have loaded AND the agent balance is actually at zero — guards against a false positive before the first fetch resolves. */
    public readonly agentExhausted = computed(() => {
        const c = this._credits();
        return !!c && c.agent.remaining <= 0;
    });
    /** True when an agent-backed surface should render the credits-exhausted screen instead of a
     *  progress/queue screen. The backend status is authoritative when present, but agents downstream
     *  of the one that ran out are never enqueued (geotech dispatch is a chain), so they stay
     *  NOT_STARTED forever and the live balance is the only signal they have. `hasData` protects
     *  records that already have content to show — including legacy ones written before the status
     *  field existed. */
    public isExhaustedFor(status?: string | null, hasData = false): boolean {
        return status === 'CREDITS_EXHAUSTED' ||
            (!hasData && this.agentExhausted() && status !== 'COMPLETED' && status !== 'FAILED');
    }

    public readonly askConsumedPct = computed(() => this.consumedPct(this._credits()?.ask ?? null));
    public readonly agentConsumedPct = computed(() => this.consumedPct(this._credits()?.agent ?? null));

    public consumedPct(entry: { consumed: number; allocated: number } | null): number | null {
        if (!entry || entry.allocated === 0) return null;
        return Math.round((entry.consumed / entry.allocated) * 100);
    }

    public fetchCredits(): Observable<any> {
        if (this.creditsCache$) {
            return this.creditsCache$;
        }

        this.creditsCache$ = this.creditsService.getCurrentCredits().pipe(
            tap((res) => {
                if (res.success && res.data) {
                    this._credits.set(res.data);
                }
            }),
            shareReplay(1)
        );

        return this.creditsCache$;
    }

    public refreshCredits(): Observable<any> {
        this.creditsCache$ = undefined;
        return this.fetchCredits();
    }

    public clearCredits(): void {
        this._credits.set(null);
        this.creditsCache$ = undefined;
    }
}
